import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  Plus, Pencil, Trash2, ChevronDown, ChevronRight, GripVertical,
  Check, X, Eye, EyeOff,
} from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';
import type { Category, Subcategory, BilingualText } from '@/types';

/* ── Inline editor for bilingual name + description ── */
interface CategoryFormData {
  nameEn: string;
  nameKa: string;
  descEn: string;
  descKa: string;
  image: string;
}

const emptyForm: CategoryFormData = { nameEn: '', nameKa: '', descEn: '', descKa: '', image: '' };

const formFromCategory = (c: Category): CategoryFormData => ({
  nameEn: c.name.en,
  nameKa: c.name.ka,
  descEn: c.description.en,
  descKa: c.description.ka,
  image: c.image || '',
});

interface SubFormData {
  nameEn: string;
  nameKa: string;
}

const emptySubForm: SubFormData = { nameEn: '', nameKa: '' };

const AdminCategoriesPage: React.FC = () => {
  const categories = useAdminStore((s) => s.categories);
  const artworks = useAdminStore((s) => s.artworks);
  const addCategory = useAdminStore((s) => s.addCategory);
  const updateCategory = useAdminStore((s) => s.updateCategory);
  const deleteCategory = useAdminStore((s) => s.deleteCategory);
  const reorderCategories = useAdminStore((s) => s.reorderCategories);
  const addSubcategory = useAdminStore((s) => s.addSubcategory);
  const updateSubcategory = useAdminStore((s) => s.updateSubcategory);
  const deleteSubcategory = useAdminStore((s) => s.deleteSubcategory);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'category' | 'subcategory'; catId: string; subId?: string } | null>(null);

  // Category form state
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [catForm, setCatForm] = useState<CategoryFormData>(emptyForm);

  // Subcategory form state
  const [showSubForm, setShowSubForm] = useState<string | null>(null); // parentCategoryId
  const [editingSubId, setEditingSubId] = useState<string | null>(null);
  const [subForm, setSubForm] = useState<SubFormData>(emptySubForm);

  const sorted = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);

  const artworkCountForCategory = (catId: string): number =>
    artworks.filter((a) => a.categoryId === catId).length;

  const artworkCountForSubcategory = (subId: string): number =>
    artworks.filter((a) => a.subcategoryId === subId).length;

  /* ── Category actions ── */
  const openAddCategory = (): void => {
    setCatForm(emptyForm);
    setEditingCatId(null);
    setShowCatForm(true);
  };

  const openEditCategory = (cat: Category): void => {
    setCatForm(formFromCategory(cat));
    setEditingCatId(cat._id);
    setShowCatForm(true);
  };

  const saveCategoryForm = (): void => {
    if (!catForm.nameEn.trim()) {
      toast.error('English name is required');
      return;
    }
    const name: BilingualText = { en: catForm.nameEn.trim(), ka: catForm.nameKa.trim() || catForm.nameEn.trim() };
    const description: BilingualText = { en: catForm.descEn.trim(), ka: catForm.descKa.trim() || catForm.descEn.trim() };

    if (editingCatId) {
      updateCategory(editingCatId, { name, description, image: catForm.image || undefined });
      toast.success('Category updated');
    } else {
      addCategory({
        name,
        description,
        image: catForm.image || undefined,
        displayOrder: categories.length + 1,
        isActive: true,
      });
      toast.success('Category created');
    }
    setShowCatForm(false);
    setCatForm(emptyForm);
    setEditingCatId(null);
  };

  const cancelCatForm = (): void => {
    setShowCatForm(false);
    setCatForm(emptyForm);
    setEditingCatId(null);
  };

  /* ── Subcategory actions ── */
  const openAddSub = (catId: string): void => {
    setSubForm(emptySubForm);
    setEditingSubId(null);
    setShowSubForm(catId);
  };

  const openEditSub = (catId: string, sub: Subcategory): void => {
    setSubForm({ nameEn: sub.name.en, nameKa: sub.name.ka });
    setEditingSubId(sub._id);
    setShowSubForm(catId);
  };

  const saveSubForm = (): void => {
    if (!showSubForm || !subForm.nameEn.trim()) {
      toast.error('English name is required');
      return;
    }
    const name: BilingualText = { en: subForm.nameEn.trim(), ka: subForm.nameKa.trim() || subForm.nameEn.trim() };

    if (editingSubId) {
      updateSubcategory(showSubForm, editingSubId, { name });
      toast.success('Subcategory updated');
    } else {
      const parent = categories.find((c) => c._id === showSubForm);
      addSubcategory(showSubForm, {
        name,
        displayOrder: parent ? parent.subcategories.length + 1 : 1,
        isActive: true,
      });
      toast.success('Subcategory created');
    }
    setShowSubForm(null);
    setSubForm(emptySubForm);
    setEditingSubId(null);
  };

  const cancelSubForm = (): void => {
    setShowSubForm(null);
    setSubForm(emptySubForm);
    setEditingSubId(null);
  };

  /* ── Delete ── */
  const handleDelete = (): void => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'category') {
      deleteCategory(deleteTarget.catId);
      toast.success('Category deleted');
      if (expandedId === deleteTarget.catId) setExpandedId(null);
    } else if (deleteTarget.subId) {
      deleteSubcategory(deleteTarget.catId, deleteTarget.subId);
      toast.success('Subcategory deleted');
    }
    setDeleteTarget(null);
  };

  /* ── Toggle active ── */
  const toggleCategoryActive = (cat: Category): void => {
    updateCategory(cat._id, { isActive: !cat.isActive });
    toast.success(cat.isActive ? 'Category hidden from shop' : 'Category visible in shop');
  };

  const toggleSubActive = (catId: string, sub: Subcategory): void => {
    updateSubcategory(catId, sub._id, { isActive: !sub.isActive });
  };

  /* ── Reorder ── */
  const handleReorder = (newOrder: Category[]): void => {
    reorderCategories(newOrder.map((c) => c._id));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Categories</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {categories.length} categories &middot; Drag to reorder
          </p>
        </div>
        <button
          onClick={openAddCategory}
          className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> Add Category
        </button>
      </div>

      {/* Add/Edit Category Form */}
      <AnimatePresence>
        {showCatForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-5 bg-white dark:bg-black border border-black/[0.06] dark:border-white/[0.06] rounded-2xl space-y-4">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                {editingCatId ? 'Edit Category' : 'New Category'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1.5">
                    Name (English) *
                  </label>
                  <input
                    value={catForm.nameEn}
                    onChange={(e) => setCatForm((f) => ({ ...f, nameEn: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[12px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all"
                    placeholder="e.g. Photography"
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1.5">
                    Name (Georgian)
                  </label>
                  <input
                    value={catForm.nameKa}
                    onChange={(e) => setCatForm((f) => ({ ...f, nameKa: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[12px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all"
                    placeholder="e.g. ფოტოგრაფია"
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1.5">
                    Description (English)
                  </label>
                  <input
                    value={catForm.descEn}
                    onChange={(e) => setCatForm((f) => ({ ...f, descEn: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[12px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all"
                    placeholder="Short description..."
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1.5">
                    Description (Georgian)
                  </label>
                  <input
                    value={catForm.descKa}
                    onChange={(e) => setCatForm((f) => ({ ...f, descKa: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[12px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all"
                    placeholder="მოკლე აღწერა..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1.5">
                    Image URL (optional)
                  </label>
                  <input
                    value={catForm.image}
                    onChange={(e) => setCatForm((f) => ({ ...f, image: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[12px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all"
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={saveCategoryForm}
                  className="flex items-center gap-1.5 px-4 py-2 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.1em] rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Check size={12} /> {editingCatId ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={cancelCatForm}
                  className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white rounded-lg transition-colors"
                >
                  <X size={12} /> Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category list with drag-to-reorder */}
      <Reorder.Group axis="y" values={sorted} onReorder={handleReorder} className="space-y-3">
        <AnimatePresence mode="popLayout">
          {sorted.map((cat) => {
            const isExpanded = expandedId === cat._id;
            const count = artworkCountForCategory(cat._id);

            return (
              <Reorder.Item
                key={cat._id}
                value={cat}
                className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all"
              >
                {/* Category row */}
                <div className="flex items-center gap-3 p-4">
                  <div className="cursor-grab active:cursor-grabbing text-black/15 dark:text-white/15 hover:text-black/40 dark:hover:text-white/40 transition-colors">
                    <GripVertical size={16} />
                  </div>

                  {cat.image && (
                    <img src={cat.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  )}

                  <button
                    onClick={() => setExpandedId(isExpanded ? null : cat._id)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <div className="min-w-0">
                      <p className="text-[13px] font-bold truncate">{cat.name.en}</p>
                      {cat.name.ka && cat.name.ka !== cat.name.en && (
                        <p className="text-[10px] text-black/30 dark:text-white/30 truncate">{cat.name.ka}</p>
                      )}
                    </div>
                  </button>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[8px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40">
                      {cat.subcategories.length} sub{cat.subcategories.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-[8px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40">
                      {count} artwork{count !== 1 ? 's' : ''}
                    </span>

                    <button
                      onClick={() => toggleCategoryActive(cat)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        cat.isActive
                          ? 'text-green-500 hover:bg-green-500/10'
                          : 'text-red-400 hover:bg-red-500/10'
                      }`}
                      title={cat.isActive ? 'Active — click to hide' : 'Hidden — click to show'}
                    >
                      {cat.isActive ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>

                    <button
                      onClick={() => openEditCategory(cat)}
                      className="p-1.5 text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] rounded-lg transition-all"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ type: 'category', catId: cat._id })}
                      className="p-1.5 text-black/30 dark:text-white/30 hover:text-red-500 hover:bg-red-500/5 rounded-lg transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Expanded: subcategories */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pl-12 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">
                            Subcategories
                          </span>
                          <button
                            onClick={() => openAddSub(cat._id)}
                            className="flex items-center gap-1 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] rounded-lg transition-all"
                          >
                            <Plus size={11} /> Add
                          </button>
                        </div>

                        {/* Subcategory list */}
                        {cat.subcategories.length === 0 && showSubForm !== cat._id && (
                          <p className="text-[10px] text-black/20 dark:text-white/20 py-2">
                            No subcategories yet
                          </p>
                        )}

                        {cat.subcategories
                          .slice()
                          .sort((a, b) => a.displayOrder - b.displayOrder)
                          .map((sub) => {
                            const subCount = artworkCountForSubcategory(sub._id);
                            return (
                              <div
                                key={sub._id}
                                className="flex items-center gap-3 py-2 px-3 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-colors group"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/10 flex-shrink-0" />
                                <span className="text-[12px] font-medium flex-1 min-w-0 truncate">
                                  {sub.name.en}
                                  {sub.name.ka && sub.name.ka !== sub.name.en && (
                                    <span className="text-black/25 dark:text-white/25 ml-2 text-[10px]">{sub.name.ka}</span>
                                  )}
                                </span>
                                <span className="text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/30 dark:text-white/30">
                                  {subCount}
                                </span>

                                <button
                                  onClick={() => toggleSubActive(cat._id, sub)}
                                  className={`p-1 rounded transition-colors ${
                                    sub.isActive ? 'text-green-500' : 'text-red-400'
                                  } opacity-0 group-hover:opacity-100`}
                                >
                                  {sub.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                                </button>
                                <button
                                  onClick={() => openEditSub(cat._id, sub)}
                                  className="p-1 text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                                >
                                  <Pencil size={12} />
                                </button>
                                <button
                                  onClick={() => setDeleteTarget({ type: 'subcategory', catId: cat._id, subId: sub._id })}
                                  className="p-1 text-black/20 dark:text-white/20 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            );
                          })}

                        {/* Inline add/edit subcategory form */}
                        <AnimatePresence>
                          {showSubForm === cat._id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="flex items-center gap-2 py-2">
                                <input
                                  value={subForm.nameEn}
                                  onChange={(e) => setSubForm((f) => ({ ...f, nameEn: e.target.value }))}
                                  className="flex-1 px-3 py-2 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-lg text-[11px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all"
                                  placeholder="Name (English)"
                                  autoFocus
                                />
                                <input
                                  value={subForm.nameKa}
                                  onChange={(e) => setSubForm((f) => ({ ...f, nameKa: e.target.value }))}
                                  className="flex-1 px-3 py-2 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-lg text-[11px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all"
                                  placeholder="Name (Georgian)"
                                />
                                <button
                                  onClick={saveSubForm}
                                  className="p-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:opacity-90 transition-opacity"
                                >
                                  <Check size={12} />
                                </button>
                                <button
                                  onClick={cancelSubForm}
                                  className="p-2 text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white rounded-lg transition-colors"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Reorder.Item>
            );
          })}
        </AnimatePresence>
      </Reorder.Group>

      {/* Empty state */}
      {categories.length === 0 && (
        <div className="text-center py-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            No categories yet
          </p>
          <button
            onClick={openAddCategory}
            className="mt-4 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
          >
            Create your first category
          </button>
        </div>
      )}

      {/* Delete dialog */}
      <AdminConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={deleteTarget?.type === 'category' ? 'Delete Category' : 'Delete Subcategory'}
        message={
          deleteTarget?.type === 'category'
            ? 'This will remove the category and unlink all associated artworks. Artworks will not be deleted. This action cannot be undone.'
            : 'This will remove the subcategory and unlink associated artworks. This action cannot be undone.'
        }
      />
    </div>
  );
};

export default AdminCategoriesPage;
